import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listOrganizationsForSelector, getSites } from '../../../services/asset.service';
import { listScannerDeploymentTokens, createScannerDeploymentToken, getScannerDeploymentToken, revokeScannerDeploymentToken, listScannerInstallAttempts, getScannerInstallAttempt, getDeploymentPlatformSettings, updateDeploymentPlatformSettings } from '../../../services/connector.service';
import '../../assets/assets.css';
import '../../scans/scans.css';
import '../../vulnerabilities/vulnerabilities.css';
import '../profile/profile.css';
import '../users-orgs/users-orgs.css';
import {
  DEPLOYMENT_ATTEMPT_TABLE_COLUMNS,
  DEPLOYMENT_ATTEMPT_TABLE_COLUMN_WIDTHS_KEY,
  DEPLOYMENT_TOKEN_TABLE_COLUMNS,
  DEPLOYMENT_TOKEN_TABLE_COLUMN_WIDTHS_KEY,
} from '../../../utils/resizableTableColumns';
import DeploymentInventoryTable from './DeploymentInventoryTable';
import './deployment.css';

const UNIVERSAL_SEARCH_MAX_LEN = 500;

const TOKEN_STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'unused', label: 'Unused' },
  { value: 'installing', label: 'Installing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'expired', label: 'Expired' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'used', label: 'Used' },
];

function sanitizeUniversalSearchInput(raw) {
  return String(raw ?? '')
    .replace(/\0/g, '')
    .slice(0, UNIVERSAL_SEARCH_MAX_LEN);
}

function DeploymentSettingsPage() {
  const DEFAULT_INSTALL_SCRIPT_URL = 'https://install.xspectre.net/scanner.sh';
  const SCANNER_TYPE_OPTIONS = useMemo(
    () => [
      { value: 'greenbone_perimeter', label: 'Network scanner — Perimeter' },
      { value: 'greenbone_onprem', label: 'Network scanner — On-prem' },
      { value: 'zap_perimeter', label: 'Web scanner — Perimeter' },
      { value: 'zap_onprem', label: 'Web scanner — On-prem' },
    ],
    []
  );

  const [organizations, setOrganizations] = useState([]);
  const [sites, setSites] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTokenId, setSelectedTokenId] = useState('');
  const [selectedAttemptId, setSelectedAttemptId] = useState('');
  const [tokenDetail, setTokenDetail] = useState(null);
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [installCommand, setInstallCommand] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [tokenSearch, setTokenSearch] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [section, setSection] = useState('general');
  const statusFilterRef = useRef(null);

  const [platformSettings, setPlatformSettings] = useState({
    partner_managed: false,
    ecommerce: false,
    disable_emails: false,
  });
  const [platformSaving, setPlatformSaving] = useState(false);

  const [createForm, setCreateForm] = useState(() => {
    const now = new Date(Date.now() + 2 * 60 * 60 * 1000);
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    return {
      customer_id: '',
      scanner_name: '',
      scanner_type: 'greenbone_perimeter',
      scanner_tags: '',
      expires_at: local,
      single_use: true,
      consume_on_failed_attempt: false,
      rport_claim_code: '',
    };
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [orgs, sitesRows, tokenRows, attemptRows, deploymentSettings] = await Promise.all([
        listOrganizationsForSelector().catch(() => []),
        getSites().catch(() => []),
        listScannerDeploymentTokens(statusFilter ? { status: statusFilter } : null),
        listScannerInstallAttempts(),
        getDeploymentPlatformSettings().catch(() => null),
      ]);

      const normalizedOrgs = Array.isArray(orgs) ? orgs : [];
      const normalizedSites = Array.isArray(sitesRows) ? sitesRows : [];
      setOrganizations(normalizedOrgs);
      setSites(normalizedSites);
      setTokens(Array.isArray(tokenRows) ? tokenRows : []);
      setAttempts(Array.isArray(attemptRows) ? attemptRows : []);

      if (deploymentSettings && typeof deploymentSettings === 'object') {
        setPlatformSettings((prev) => ({
          ...prev,
          partner_managed: Boolean(deploymentSettings.partner_managed),
          ecommerce: Boolean(deploymentSettings.ecommerce),
          disable_emails: Boolean(deploymentSettings.disable_emails),
        }));
      }

      if (!createForm.customer_id && normalizedOrgs.length > 0) {
        const firstId = String(normalizedOrgs[0]?.uu_id || normalizedOrgs[0]?.id || '').trim();
        if (firstId) {
          setCreateForm((prev) => ({ ...prev, customer_id: firstId }));
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load scanner deployment data.');
    } finally {
      setLoading(false);
    }
  }, [createForm.customer_id, statusFilter]);

  const availableSites = useMemo(() => {
    const customerId = String(createForm.customer_id || '').trim();
    if (!customerId) return [];
    return (Array.isArray(sites) ? sites : []).filter((site) => {
      const orgId = String(
        site?.org_uuid || site?.organization_uuid || site?.organization_id || site?.organization || ''
      ).trim();
      return orgId === customerId;
    });
  }, [createForm.customer_id, sites]);

  useEffect(() => {
    const currentSiteId = String(selectedSiteId || '').trim();
    if (!currentSiteId) return;
    const exists = availableSites.some((site) => {
      const candidateSiteId = String(site?.uu_id || site?.site_uuid || site?.id || '').trim();
      return candidateSiteId === currentSiteId;
    });
    if (!exists) {
      setSelectedSiteId('');
    }
  }, [availableSites, selectedSiteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const onDoc = (event) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedStatusLabel =
    TOKEN_STATUS_FILTER_OPTIONS.find((opt) => opt.value === statusFilter)?.label || 'All statuses';

  useEffect(() => {
    if (!selectedTokenId) {
      setTokenDetail(null);
      return;
    }
    let cancelled = false;
    getScannerDeploymentToken(selectedTokenId)
      .then((detail) => {
        if (!cancelled) setTokenDetail(detail);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load token detail.');
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTokenId]);

  useEffect(() => {
    if (!selectedAttemptId) {
      setAttemptDetail(null);
      return;
    }
    let cancelled = false;
    getScannerInstallAttempt(selectedAttemptId)
      .then((detail) => {
        if (!cancelled) setAttemptDetail(detail);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Failed to load attempt detail.');
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAttemptId]);

  const filteredTokens = useMemo(() => {
    const q = String(tokenSearch || '').trim().toLowerCase();
    if (!q) return tokens;
    return tokens.filter((item) => {
      const blob = [
        item?.scanner_name,
        item?.scanner_id,
        item?.customer_id,
        item?.status,
        item?.token_preview,
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [tokenSearch, tokens]);

  const STATUS_LABELS = {
    unused: 'Unused',
    installing: 'Installing',
    completed: 'Completed',
    success: 'Completed',
    failed: 'Failed',
    expired: 'Expired',
    revoked: 'Revoked',
    used: 'Used',
  };

  const statusClass = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'completed' || s === 'success') return 'deployment-status-chip--completed';
    if (s === 'installing') return 'deployment-status-chip--installing';
    if (s === 'failed') return 'deployment-status-chip--failed';
    if (s === 'expired') return 'deployment-status-chip--expired';
    if (s === 'revoked') return 'deployment-status-chip--revoked';
    if (s === 'used') return 'deployment-status-chip--used';
    return 'deployment-status-chip--unused';
  };

  const statusLabel = (status) => {
    const s = String(status || '').toLowerCase().trim();
    return STATUS_LABELS[s] || (s ? s.replace(/_/g, ' ') : 'Unknown');
  };

  const prettyDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  };

  const scannerTypeLabel = (value) => {
    const found = SCANNER_TYPE_OPTIONS.find((item) => item.value === value);
    return found ? found.label : String(value || '');
  };

  const savePlatformSettings = useCallback(async (nextSettings) => {
    setPlatformSaving(true);
    setError('');
    setMessage('');
    try {
      const updated = await updateDeploymentPlatformSettings({
        partner_managed: Boolean(nextSettings.partner_managed),
        ecommerce: Boolean(nextSettings.ecommerce),
        disable_emails: Boolean(nextSettings.disable_emails),
      });
      setPlatformSettings((prev) => ({
        ...prev,
        partner_managed: Boolean(updated?.partner_managed ?? nextSettings.partner_managed),
        ecommerce: Boolean(updated?.ecommerce ?? nextSettings.ecommerce),
        disable_emails: Boolean(updated?.disable_emails ?? nextSettings.disable_emails),
      }));
      setMessage('General deployment settings saved.');
    } catch (err) {
      setError(err?.message || 'Failed to save deployment settings.');
    } finally {
      setPlatformSaving(false);
    }
  }, []);

  const handlePlatformToggle = useCallback((key, checked) => {
    setPlatformSettings((prev) => {
      const next = { ...prev, [key]: checked };
      void savePlatformSettings(next);
      return next;
    });
  }, [savePlatformSettings]);

  const handleCreateToken = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        customer_id: createForm.customer_id,
        ...(selectedSiteId ? { site_id: selectedSiteId } : {}),
        scanner_name: createForm.scanner_name.trim(),
        scanner_type: createForm.scanner_type,
        scanner_tags: createForm.scanner_tags
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean),
        expires_at: new Date(createForm.expires_at).toISOString(),
        single_use: Boolean(createForm.single_use),
        consume_on_failed_attempt: Boolean(createForm.consume_on_failed_attempt),
        rport_claim_code: createForm.rport_claim_code.trim(),
      };
      const created = await createScannerDeploymentToken(payload);
      const serverCommand = String(created?.install_command || '').trim();
      const plainToken = String(created?.token || '').trim();
      const fallbackCommand = plainToken
        ? `curl -fsSL ${DEFAULT_INSTALL_SCRIPT_URL} | sudo bash -s -- --token ${plainToken}`
        : '';
      setInstallCommand(serverCommand || fallbackCommand);
      setMessage('Token created. Copy the install command below — the plain token is shown once.');
      setSelectedTokenId(String(created?.token_id || ''));
      setSection('tokens');
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to create deployment token.');
    } finally {
      setCreating(false);
    }
  };

  const copyText = async (value, okMessage) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setMessage(okMessage || 'Copied to clipboard.');
    } catch {
      setError('Clipboard copy failed.');
    }
  };

  const handleRevokeToken = async () => {
    if (!selectedTokenId) return;
    const reason = window.prompt('Revocation reason', 'Revoked by admin');
    if (reason === null) return;
    setRevoking(true);
    setError('');
    setMessage('');
    try {
      await revokeScannerDeploymentToken(selectedTokenId, reason);
      setMessage('Token revoked successfully.');
      await loadData();
      const detail = await getScannerDeploymentToken(selectedTokenId);
      setTokenDetail(detail);
    } catch (err) {
      setError(err?.message || 'Failed to revoke token.');
    } finally {
      setRevoking(false);
    }
  };

  const auditSummary = useMemo(() => {
    const source = tokenDetail || {};
    const tokenAttempts = Array.isArray(source?.attempts) ? source.attempts : [];
    const counts = tokenAttempts.reduce(
      (acc, item) => {
        const s = String(item?.status || '').toLowerCase();
        if (s === 'success') acc.success += 1;
        else if (s === 'failed') acc.failed += 1;
        else if (s === 'installing') acc.installing += 1;
        return acc;
      },
      { success: 0, failed: 0, installing: 0 }
    );
    return {
      total: tokenAttempts.length,
      ...counts,
      latestStatusSummary: source?.latest_status_summary || '',
      latestError: source?.latest_error_message || source?.latest_error_code || '',
      latestHealthcheck: source?.latest_healthcheck || {},
    };
  }, [tokenDetail]);

  const deploymentTabs = [
    {
      id: 'general',
      label: 'General',
      icon: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6.1 6.1l1.6 1.6M16.3 16.3l1.6 1.6M17.9 6.1l-1.6 1.6M7.7 16.3l-1.6 1.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      id: 'tokens',
      label: 'Scanner tokens',
      icon: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="8" cy="15" r="3.2" stroke="currentColor" strokeWidth="1.8" />
          <path d="M10.4 12.6 19.5 3.5M16.2 6.8l2.2 2.2M14 9l2.2 2.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
    {
      id: 'attempts',
      label: 'Install attempts',
      icon: (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 7.75V12l3.1 2.05" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
    },
  ];

  useEffect(() => {
    const onTab = (event) => {
      const tab = String(event?.detail?.tab || '').trim();
      if (tab === 'general' || tab === 'tokens' || tab === 'attempts') setSection(tab);
    };
    const onSelectFirstAttempt = () => {
      setSection('attempts');
      setSelectedAttemptId((current) => {
        if (current) return current;
        const firstId = String(attempts[0]?.install_id || '').trim();
        return firstId || current;
      });
    };
    window.addEventListener('xspectre:tour-deployment-tab', onTab);
    window.addEventListener('xspectre:tour-select-first-attempt', onSelectFirstAttempt);
    return () => {
      window.removeEventListener('xspectre:tour-deployment-tab', onTab);
      window.removeEventListener('xspectre:tour-select-first-attempt', onSelectFirstAttempt);
    };
  }, [attempts]);

  return (
    <div className="deployment-page" aria-label="Deployment settings">
      <p className="deployment-page-intro">
        Platform options, scanner install tokens, and install history.
      </p>

      {error ? <div className="profile-settings-message profile-settings-error" role="alert">{error}</div> : null}
      {message ? <div className="profile-settings-message profile-settings-success" role="status">{message}</div> : null}

      <div
        className="scans-network-scope-tabs deployment-scope-tabs"
        data-tour="settings-deployment-tabs"
        role="tablist"
        aria-label="Deployment sections"
      >
        <div className="scans-network-scope-tabs-track deployment-scope-tabs-track">
          {deploymentTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              data-tour={`settings-deployment-tab-${tab.id}`}
              aria-selected={section === tab.id}
              className={`scans-network-scope-tab${section === tab.id ? ' is-active' : ''}`}
              onClick={() => setSection(tab.id)}
              disabled={platformSaving}
            >
              <span className="scans-network-scope-tab-main">
                <span className="scans-network-scope-tab-icon" aria-hidden="true">{tab.icon}</span>
                <span className="scans-network-scope-tab-label">{tab.label}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {section === 'general' ? (
        <section
          className="deployment-section-card deployment-section-card--general"
          data-tour="settings-deployment-general"
          aria-label="Platform options"
        >
          <div
            className={`deployment-toggle-list${platformSaving ? ' is-saving' : ''}`}
            role="group"
            aria-label="Platform options"
            aria-busy={platformSaving || undefined}
          >
            <label className="deployment-toggle-row">
              <span className="deployment-toggle-copy">
                <strong>Partner management</strong>
                <small>Allow partner-managed customer workflows.</small>
              </span>
              <span className="deployment-toggle-switch" aria-hidden="true">
                <span className="deployment-toggle-switch-thumb" />
              </span>
              <input
                type="checkbox"
                className="deployment-toggle-input"
                checked={Boolean(platformSettings.partner_managed)}
                disabled={platformSaving || loading}
                onChange={(e) => handlePlatformToggle('partner_managed', e.target.checked)}
              />
            </label>
            <label className="deployment-toggle-row">
              <span className="deployment-toggle-copy">
                <strong>E-commerce</strong>
                <small>Turn on storefront billing flows.</small>
              </span>
              <span className="deployment-toggle-switch" aria-hidden="true">
                <span className="deployment-toggle-switch-thumb" />
              </span>
              <input
                type="checkbox"
                className="deployment-toggle-input"
                checked={Boolean(platformSettings.ecommerce)}
                disabled={platformSaving || loading}
                onChange={(e) => handlePlatformToggle('ecommerce', e.target.checked)}
              />
            </label>
            <label className="deployment-toggle-row">
              <span className="deployment-toggle-copy">
                <strong>Disable emails</strong>
                <small>Stop outbound platform emails.</small>
              </span>
              <span className="deployment-toggle-switch" aria-hidden="true">
                <span className="deployment-toggle-switch-thumb" />
              </span>
              <input
                type="checkbox"
                className="deployment-toggle-input"
                checked={Boolean(platformSettings.disable_emails)}
                disabled={platformSaving || loading}
                onChange={(e) => handlePlatformToggle('disable_emails', e.target.checked)}
              />
            </label>
          </div>
        </section>
      ) : null}

      {section === 'tokens' ? (
        <div data-tour="settings-deployment-tokens">
          <section className="deployment-section-card" aria-labelledby="deployment-create-heading">
            <div className="deployment-section-head">
              <h2 className="deployment-section-title" id="deployment-create-heading">Create token</h2>
              <p className="deployment-section-desc">
                Generate a one-time install token for a customer scanner. The install command is shown once.
              </p>
            </div>
            <form onSubmit={handleCreateToken}>
              <div className="deployment-form-grid">
                <div className="profile-settings-field">
                  <label htmlFor="deployment-customer">Customer</label>
                  <select
                    id="deployment-customer"
                    className="profile-settings-input"
                    value={createForm.customer_id}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, customer_id: e.target.value }))}
                    required
                  >
                    <option value="">Select customer</option>
                    {organizations.map((org, idx) => {
                      const id = String(org?.uu_id || org?.id || '').trim();
                      const name = String(org?.name || org?.org_name || `Organization ${idx + 1}`);
                      return (
                        <option key={id || `org-${idx}`} value={id}>{name}</option>
                      );
                    })}
                  </select>
                </div>
                <div className="profile-settings-field">
                  <label htmlFor="deployment-scanner-name">Scanner name</label>
                  <input
                    id="deployment-scanner-name"
                    className="profile-settings-input"
                    type="text"
                    value={createForm.scanner_name}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, scanner_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="profile-settings-field">
                  <label htmlFor="deployment-site">Site</label>
                  <select
                    id="deployment-site"
                    className="profile-settings-input"
                    value={selectedSiteId}
                    onChange={(e) => setSelectedSiteId(e.target.value)}
                  >
                    <option value="">Organization-level (unassigned)</option>
                    {availableSites.map((site, idx) => {
                      const id = String(site?.uu_id || site?.site_uuid || site?.id || '').trim();
                      const name = String(site?.site_name || site?.name || `Site ${idx + 1}`);
                      return (
                        <option key={id || `site-${idx}`} value={id}>{name}</option>
                      );
                    })}
                  </select>
                </div>
                <div className="profile-settings-field">
                  <label htmlFor="deployment-scanner-type">Scanner type</label>
                  <select
                    id="deployment-scanner-type"
                    className="profile-settings-input"
                    value={createForm.scanner_type}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, scanner_type: e.target.value }))}
                  >
                    {SCANNER_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="profile-settings-field">
                  <label htmlFor="deployment-expires">Expires</label>
                  <input
                    id="deployment-expires"
                    className="profile-settings-input"
                    type="datetime-local"
                    value={createForm.expires_at}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, expires_at: e.target.value }))}
                    required
                  />
                </div>
                <div className="profile-settings-field">
                  <label htmlFor="deployment-tags">Tags</label>
                  <input
                    id="deployment-tags"
                    className="profile-settings-input"
                    type="text"
                    value={createForm.scanner_tags}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, scanner_tags: e.target.value }))}
                    placeholder="perimeter, critical"
                  />
                </div>
                <div className="profile-settings-field deployment-form-span">
                  <label htmlFor="deployment-rport">RPort claim code</label>
                  <input
                    id="deployment-rport"
                    className="profile-settings-input"
                    type="text"
                    value={createForm.rport_claim_code}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, rport_claim_code: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="deployment-form-footer">
                <div className="deployment-form-options" role="group" aria-label="Token options">
                  <label className="deployment-option-check" title="Token can only be used for one successful install">
                    <input
                      type="checkbox"
                      className="deployment-settings-checkbox"
                      checked={createForm.single_use}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, single_use: e.target.checked }))}
                    />
                    <span>One install only</span>
                  </label>
                  <label className="deployment-option-check" title="A failed install also uses up a one-install token">
                    <input
                      type="checkbox"
                      className="deployment-settings-checkbox"
                      checked={createForm.consume_on_failed_attempt}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, consume_on_failed_attempt: e.target.checked }))}
                    />
                    <span>Count failed installs</span>
                  </label>
                </div>
                <button
                  type="submit"
                  className="deployment-btn deployment-btn--primary deployment-form-submit"
                  disabled={creating || loading}
                >
                  Create token
                </button>
              </div>
            </form>
            {installCommand ? (
              <div className="deployment-install-banner">
                <code>{installCommand}</code>
                <button
                  type="button"
                  className="deployment-btn deployment-btn--primary"
                  onClick={() => copyText(installCommand, 'Install command copied.')}
                >
                  Copy command
                </button>
              </div>
            ) : null}
          </section>

          <div className="deployment-split">
          <section className="deployment-section-card" aria-labelledby="deployment-tokens-heading">
            <div className="deployment-section-head">
              <h2 className="deployment-section-title" id="deployment-tokens-heading">Tokens</h2>
              <p className="deployment-section-desc">Select a token to inspect status, attempts, and healthcheck.</p>
            </div>
            <div className="manage-users-filters-row partners-settings-filters-row deployment-toolbar">
              <div className="risks-page-filters">
                <div
                  className="users-orgs-users-company-filter users-orgs-role-filter-field"
                  ref={statusFilterRef}
                >
                  <div className="users-orgs-users-company-filter-bar">
                    <button
                      type="button"
                      className={`users-orgs-users-company-filter-main${!statusFilter ? ' users-orgs-users-company-filter-main--all-orgs' : ''}`}
                      aria-expanded={statusFilterOpen}
                      aria-haspopup="listbox"
                      aria-label="Filter tokens by status"
                      onClick={() => setStatusFilterOpen((open) => !open)}
                    >
                      <svg
                        className="users-orgs-users-company-filter-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                      </svg>
                      <span
                        className={`users-orgs-users-company-filter-label${statusFilter ? ' users-orgs-users-company-filter-label--active' : ''}`}
                      >
                        {selectedStatusLabel}
                      </span>
                      <span className="users-orgs-users-company-filter-chevron" aria-hidden="true" />
                    </button>
                    {statusFilter ? (
                      <button
                        type="button"
                        className="users-orgs-users-company-filter-clear"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setStatusFilter('');
                          setStatusFilterOpen(false);
                        }}
                        aria-label="Clear status filter"
                        title="Clear status filter"
                      >
                        <svg
                          width={14}
                          height={14}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                  {statusFilterOpen ? (
                    <div className="users-orgs-users-company-filter-panel" role="presentation">
                      <ul className="users-orgs-users-company-filter-list" role="listbox">
                        {TOKEN_STATUS_FILTER_OPTIONS.map((opt) => (
                          <li key={`token-status-${opt.value || 'all'}`} role="presentation">
                            <button
                              type="button"
                              role="option"
                              aria-selected={statusFilter === opt.value}
                              className={`users-orgs-users-company-filter-option${statusFilter === opt.value ? ' is-selected' : ''}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setStatusFilter(opt.value);
                                setStatusFilterOpen(false);
                              }}
                            >
                              {opt.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
                <div className="subscription-org-management-search manage-users-inline-search">
                  <label htmlFor="deployment-token-search" className="edi-sr-only">
                    Search tokens
                  </label>
                  <div className="subscription-org-management-search-inner">
                    <input
                      id="deployment-token-search"
                      type="search"
                      className="subscription-org-management-search-input"
                      placeholder="Search tokens"
                      value={tokenSearch}
                      onChange={(e) => setTokenSearch(sanitizeUniversalSearchInput(e.target.value))}
                      autoComplete="off"
                      spellCheck={false}
                      maxLength={UNIVERSAL_SEARCH_MAX_LEN}
                    />
                    {tokenSearch ? (
                      <button
                        type="button"
                        className="ghost-exposure-sys-table-search-clear"
                        onClick={() => setTokenSearch('')}
                        aria-label="Clear token search"
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <DeploymentInventoryTable
              columns={DEPLOYMENT_TOKEN_TABLE_COLUMNS}
              widthsKey={DEPLOYMENT_TOKEN_TABLE_COLUMN_WIDTHS_KEY}
              rows={loading ? [] : filteredTokens}
              emptyLabel={loading ? 'Loading tokens…' : 'No tokens found.'}
              selectedKey={selectedTokenId}
              getRowKey={(row) => row.token_id}
              onSelectRow={(row) => setSelectedTokenId(row.token_id)}
              renderRow={(row) => (
                <>
                  <td>{row.scanner_name}</td>
                  <td>{scannerTypeLabel(row.scanner_type)}</td>
                  <td><span className={`deployment-status-chip ${statusClass(row.status)}`}>{statusLabel(row.status)}</span></td>
                  <td>{row.token_preview}</td>
                  <td>{prettyDate(row.expires_at)}</td>
                </>
              )}
            />
          </section>
          <aside className="deployment-section-card deployment-detail-card" aria-labelledby="deployment-token-detail-heading">
            <h3 className="deployment-section-title" id="deployment-token-detail-heading">Token detail</h3>
            {!tokenDetail ? (
              <p className="deployment-empty">Select a token to see details.</p>
            ) : (
              <>
                <dl className="deployment-kv-grid">
                  <dt>Token ID</dt>
                  <dd><code>{tokenDetail.token_id}</code></dd>
                  <dt>Customer</dt>
                  <dd><code>{tokenDetail.customer_id}</code></dd>
                  <dt>Site</dt>
                  <dd>{tokenDetail.site_id || 'Unassigned'}</dd>
                  <dt>Scanner</dt>
                  <dd>{tokenDetail.scanner_name} ({tokenDetail.scanner_id})</dd>
                  <dt>Type</dt>
                  <dd>{scannerTypeLabel(tokenDetail.scanner_type)}</dd>
                  <dt>Status</dt>
                  <dd><span className={`deployment-status-chip ${statusClass(tokenDetail.status)}`}>{statusLabel(tokenDetail.status)}</span></dd>
                  <dt>Environment</dt>
                  <dd>{tokenDetail.allowed_environment || '—'}</dd>
                  <dt>Summary</dt>
                  <dd>{auditSummary.latestStatusSummary || '—'}</dd>
                  <dt>Error</dt>
                  <dd>{auditSummary.latestError || '—'}</dd>
                  <dt>Attempts</dt>
                  <dd>
                    {auditSummary.total} total · {auditSummary.success} success · {auditSummary.failed} failed · {auditSummary.installing} installing
                  </dd>
                </dl>
                <details className="deployment-json-block">
                  <summary>Latest healthcheck</summary>
                  <pre>{JSON.stringify(auditSummary.latestHealthcheck || {}, null, 2)}</pre>
                </details>
                <div className="deployment-form-footer">
                  <button
                    type="button"
                    className="deployment-btn deployment-btn--danger"
                    onClick={handleRevokeToken}
                    disabled={revoking || tokenDetail.status === 'revoked' || tokenDetail.status === 'used'}
                  >
                    Revoke token
                  </button>
                  <button
                    type="button"
                    className="deployment-btn"
                    onClick={() => copyText(tokenDetail.token_id, 'Token ID copied.')}
                  >
                    Copy token ID
                  </button>
                </div>
              </>
            )}
          </aside>
        </div>
        </div>
      ) : null}

      {section === 'attempts' ? (
        <div className="deployment-split">
          <section
            className="deployment-section-card"
            data-tour="settings-deployment-attempts"
            aria-labelledby="deployment-attempts-heading"
          >
            <div className="deployment-section-head">
              <h2 className="deployment-section-title" id="deployment-attempts-heading">Install attempts</h2>
              <p className="deployment-section-desc">Select an attempt to inspect host healthcheck data.</p>
            </div>
            <DeploymentInventoryTable
              columns={DEPLOYMENT_ATTEMPT_TABLE_COLUMNS}
              widthsKey={DEPLOYMENT_ATTEMPT_TABLE_COLUMN_WIDTHS_KEY}
              rows={attempts}
              emptyLabel="No install attempts."
              selectedKey={selectedAttemptId}
              getRowKey={(row) => row.install_id}
              onSelectRow={(row) => setSelectedAttemptId(row.install_id)}
              renderRow={(row) => (
                <>
                  <td className="assets-cell-ellipsis" title={row.install_id}><code>{row.install_id}</code></td>
                  <td>
                    <span className={`deployment-status-chip ${statusClass(row.status)}`}>
                      {statusLabel(row.status)}
                    </span>
                  </td>
                  <td>{row.host_fingerprint || '—'}</td>
                  <td>{prettyDate(row.last_check_in_at) || '—'}</td>
                </>
              )}
            />
          </section>
          <aside
            className="deployment-section-card deployment-detail-card"
            data-tour="settings-deployment-attempt-detail"
            data-filled={attemptDetail ? 'true' : 'false'}
            aria-labelledby="deployment-attempt-detail-heading"
          >
            <h3 className="deployment-section-title" id="deployment-attempt-detail-heading">Attempt detail</h3>
            {!attemptDetail ? (
              <p className="deployment-empty">Select an attempt to see details.</p>
            ) : (
              <>
                <dl className="deployment-kv-grid">
                  <dt>Install ID</dt>
                  <dd><code>{attemptDetail.install_id}</code></dd>
                  <dt>Status</dt>
                  <dd>
                    <span className={`deployment-status-chip ${statusClass(attemptDetail.status)}`}>
                      {statusLabel(attemptDetail.status)}
                    </span>
                  </dd>
                  <dt>Host</dt>
                  <dd>{attemptDetail.host_fingerprint || '—'}</dd>
                  <dt>Installer</dt>
                  <dd>{attemptDetail.installer_version || '—'}</dd>
                  <dt>Summary</dt>
                  <dd>{attemptDetail.status_summary || '—'}</dd>
                  <dt>Error</dt>
                  <dd>
                    {attemptDetail.error_code || attemptDetail.error_message
                      ? `${attemptDetail.error_code || ''} ${attemptDetail.error_message || ''}`.trim()
                      : '—'}
                  </dd>
                  <dt>Token status</dt>
                  <dd>{attemptDetail?.token?.status || '—'}</dd>
                </dl>
                <details className="deployment-json-block">
                  <summary>Healthcheck payload</summary>
                  <pre>{JSON.stringify(attemptDetail.healthcheck || {}, null, 2)}</pre>
                </details>
              </>
            )}
          </aside>
        </div>
      ) : null}
    </div>
  );
}

export default DeploymentSettingsPage;
